import { useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

interface CaseReportReviewActionsProps {
    report: any;
    hasReportApproval?: boolean;
    isAuthor?: boolean;
    canUnapprove?: boolean;
    onSubmitForApproval?: () => void;
    onReturnToAuthor?: () => void;
    onApprove?: () => void;
    onUnapprove?: () => void;
}

export function CaseReportReviewActions({
    report,
    hasReportApproval = false,
    isAuthor = false,
    canUnapprove = false,
    onSubmitForApproval,
    onReturnToAuthor,
    onApprove,
    onUnapprove,
}: CaseReportReviewActionsProps) {
    const { theme } = useTheme();

    const isPendingFile = useMemo(() => !report.isSubmitted, [report.isSubmitted]);
    const isPendingApproval = useMemo(() => report.isSubmitted && !report.isApproved, [report.isSubmitted, report.isApproved]);
    const isApproved = useMemo(() => report.isApproved, [report.isApproved]);

    const showSubmitForApproval = useMemo(() => isPendingFile, [isPendingFile]);
    const showApproveReturn = useMemo(() => isPendingApproval && hasReportApproval, [isPendingApproval, hasReportApproval]);
    const showUnapprove = useMemo(() => isApproved && canUnapprove, [isApproved, canUnapprove]);

    const panelTitle = useMemo(() => {
        if (isPendingFile) return 'Submit / Audit';
        if (isPendingApproval) return 'Approve / Return';
        if (isApproved) return 'Report Approved';
        return 'Actions';
    }, [isPendingFile, isPendingApproval, isApproved]);

    return (
        <div className={'case-report-review-actions' + (theme ? ' ' + theme : '')}>
            <div className="panel">
                <div className="k-header k-grid-toolbar">{panelTitle}</div>
                <div className="panel-body">
                    <div className="review-actions-buttons">
                        {/* Pending File State */}
                        {showSubmitForApproval && (
                            <>
                                <p className="review-hint">Use the Save and Exit buttons in the header to save your work or exit.</p>
                                <button className="btn btn-success btn-lg" onClick={() => onSubmitForApproval?.()}>
                                    <i className="fa fa-paper-plane"></i> Submit for Approval
                                </button>
                            </>
                        )}

                        {/* Pending Approval State */}
                        {showApproveReturn && (
                            <>
                                <button className="btn btn-warning" onClick={() => onReturnToAuthor?.()}>
                                    <i className="fa fa-undo"></i> Return to Author
                                </button>
                                <button className="btn btn-success" onClick={() => onApprove?.()}>
                                    <i className="fa fa-check"></i> Approve Report
                                </button>
                            </>
                        )}

                        {/* Approved State */}
                        {isApproved && (
                            <>
                                <div className="approved-message">
                                    <i className="fa fa-check-circle text-success"></i>
                                    This report has been approved.
                                </div>
                                {showUnapprove && (
                                    <button
                                        className="btn btn-warning"
                                        onClick={() => onUnapprove?.()}
                                    >
                                        <i className="fa fa-undo"></i> Unapprove Report
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

(window as any).CaseReportReviewActionsReact = CaseReportReviewActions;
